import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import CodeInput from "components/common/CodeInput";

/**
 * SixDigitCodeInput — a 6-digit sign-in or verification code (MFA, SMS), as the shared one-box-
 * per-digit CodeInput grouped 3 – 3, so every code in the app is entered the same way.
 *
 * Uncontrolled, for the auth screens that use it:
 * @param {function} onComplete  - called with the 6-digit string when all digits are filled
 * @param {function} [onChange]  - called with the current digits on every change
 * @param {boolean}  [disabled]
 * @param {boolean}  [autoFocus] - focuses the first box on mount
 *
 * Ref exposes: reset() — clears all boxes and refocuses the first; focus().
 */
const SixDigitCodeInput = forwardRef(
  ({ onComplete, onChange, disabled = false, autoFocus = false }, ref) => {
    const [code, setCode] = useState("");
    const inputRef = useRef(null);

    useImperativeHandle(ref, () => ({
      reset() {
        setCode("");
        onChange?.("");
        inputRef.current?.focus();
      },
      focus() {
        inputRef.current?.focus();
      },
    }));

    return (
      <CodeInput
        ref={inputRef}
        value={code}
        onChange={(digits) => {
          setCode(digits);
          onChange?.(digits);
        }}
        onComplete={(digits) => {
          if (!disabled) onComplete?.(digits);
        }}
        groups={[3, 3]}
        disabled={disabled}
        autoFocus={autoFocus}
        ariaLabel="Verification code"
      />
    );
  },
);

SixDigitCodeInput.displayName = "SixDigitCodeInput";

export default SixDigitCodeInput;
